/**
 * overridesRuntime — estado de Modo Edición (guardado directo, ARS93
 * 2026-09-10). Reemplaza el patrón "exportar snippet → pegar a mano →
 * deploy": los 7 arrays de aquí abajo se rellenan con lo que devuelve
 * `GET /api/overrides` (auth-gateway) al arrancar la app y cada vez que
 * Modo Edición guarda un cambio (ver `refreshOverrides.ts`, el orquestador).
 *
 * Se mutan in-place (`.length = 0; .push(...)`), nunca se reasigna el
 * binding — así `santa-coloma.ts`/`osm-group-mapping-resolved.ts` importan
 * estos arrays una sola vez y siempre ven el contenido actual.
 *
 * A propósito, este módulo NO importa nada de `santa-coloma.ts` ni de
 * `osm-group-mapping-resolved.ts` (aunque ambos importan de aquí) — hacerlo
 * crea un ciclo de 3 módulos cuyo orden de evaluación real depende del
 * grafo completo de imports de la app, no solo de estos archivos, y puede
 * intentar leer un binding antes de inicializarse
 * (`ReferenceError: Cannot access '...' before initialization`, visto en
 * producción). El orquestador que sí conoce los 3 archivos es
 * `refreshOverrides.ts`, y no lo importa ninguno de ellos — así no hay
 * ciclo.
 */
export interface PositionOverride {
  logicalId: number;
  cabinet: [number, number]; // [lng, lat]
  moveCenter: boolean;
}

export interface OsmGroupOverride {
  osmId: number;
  cabinetId: number;
  groupId: number;
}

export interface GroupBearingOverride {
  cabinetId: number;
  groupId: number;
  bearing: number; // degrees, 0=north, [0,360)
}

// Exclusión de un cableado semáforo→grupo (2026-09-15) — filtro final sobre
// el mapeo ya resuelto (automático de gen-osm-mapping.py + manual de
// group_wiring), sin importar de dónde venía la asignación. Ver
// `applyGroupWiringExclusions.ts`.

export interface SignalPositionOverride {
  id: number; // ID del nodo OSM
  position: [number, number];
}

export interface NewRegulatorOverride {
  logicalId: number;
  position: [number, number];
  name?: string;
}

export interface NewSignalOverride {
  id: number;
  position: [number, number];
}

/** Código de alarma propio de este municipio (SC-43) — ARS93 pidió poder
 * dar de alta alarmas que no están en el catálogo estándar del Protocolo B
 * (ej. "ventilador roto"). Solo cubre la traducción código→texto; que la
 * alarma llegue a publicarse de verdad depende del regulador/gateway. */
export interface CustomAlarmCodeOverride {
  code: string;
  description: string;
}

export interface OverridesPayload {
  regulatorPosition: PositionOverride[];
  groupWiring: OsmGroupOverride[];
  signalPosition: SignalPositionOverride[];
  regulatorAdded: NewRegulatorOverride[];
  regulatorRemoved: number[];
  signalAdded: NewSignalOverride[];
  signalRemoved: number[];
  alarmCode: CustomAlarmCodeOverride[];
  groupBearing: GroupBearingOverride[];
  groupWiringExcluded: number[];
}

export const positionOverrides: PositionOverride[] = [];
export const osmGroupOverrides: OsmGroupOverride[] = [];
export const signalPositionOverrides: SignalPositionOverride[] = [];
export const addedRegulators: NewRegulatorOverride[] = [];
export const removedRegulatorIds: number[] = [];
export const addedSignals: NewSignalOverride[] = [];
export const removedSignalIds: number[] = [];
export const customAlarmCodes: CustomAlarmCodeOverride[] = [];
export const groupBearingOverrides: GroupBearingOverride[] = [];
export const excludedOsmIds: number[] = [];

function replaceContents<T>(arr: T[], next: T[]): void {
  arr.length = 0;
  arr.push(...next);
}

/** Puebla los 9 arrays desde un payload fresco de /api/overrides. No recalcula
 * nada derivado (`santaColomaIntersections`, etc.) — eso lo hace
 * `refreshOverrides.ts` justo después, para no crear un ciclo de módulos. */
export function setOverridesData(payload: OverridesPayload): void {
  replaceContents(positionOverrides, payload.regulatorPosition ?? []);
  replaceContents(osmGroupOverrides, payload.groupWiring ?? []);
  replaceContents(signalPositionOverrides, payload.signalPosition ?? []);
  replaceContents(addedRegulators, payload.regulatorAdded ?? []);
  replaceContents(removedRegulatorIds, payload.regulatorRemoved ?? []);
  replaceContents(addedSignals, payload.signalAdded ?? []);
  replaceContents(removedSignalIds, payload.signalRemoved ?? []);
  replaceContents(customAlarmCodes, payload.alarmCode ?? []);
  replaceContents(groupBearingOverrides, payload.groupBearing ?? []);
  replaceContents(excludedOsmIds, payload.groupWiringExcluded ?? []);
}
